// Phase 2 — Shared component library sandbox (dev only, route /components-demo).
// Exercises every Phase 2 component with mock data. Not part of the product UI.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  Boxes,
  IndianRupee,
  MoreVertical,
  PackageCheck,
  ShoppingBag,
  TrendingUp,
} from "lucide-react";
import { DataTable } from "./data-table";
import { PageHeader } from "./page-header";
import { StatCard } from "./stat-card";
import { StatusBadge } from "./status-badge";
import { FormSection, FieldRow } from "./form-section";
import { Stepper } from "./stepper";
import { FileDropzone, type Uploader } from "./file-dropzone";
import { ConfirmDialog } from "./confirm-dialog";
import { LoadingOverlay } from "./loading-overlay";
import { EmptyState } from "./empty-state";
import { ThemeToggle } from "./theme-toggle";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Spinner } from "ui/spinner";
import { toast } from "ui/sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";

type Product = {
  id: string;
  name: string;
  category: string;
  size: string;
  mrp: number;
  available: number;
  status: string;
};

const ALL_PRODUCTS: Product[] = [
  { id: "PRD-1001", name: "Cotton Kurti", category: "Ethnic Wear", size: "M", mrp: 1299, available: 42, status: "In Stock" },
  { id: "PRD-1002", name: "Silk Saree", category: "Sarees", size: "Free", mrp: 4999, available: 8, status: "Yet to start" },
  { id: "PRD-1003", name: "Denim Jacket", category: "Western", size: "L", mrp: 2499, available: 0, status: "Sold" },
  { id: "PRD-1004", name: "Linen Shirt", category: "Western", size: "XL", mrp: 1799, available: 15, status: "Stickering Completed" },
  { id: "PRD-1005", name: "Palazzo Pants", category: "Bottom Wear", size: "S", mrp: 899, available: 3, status: "Damaged" },
  { id: "PRD-1006", name: "Chiffon Dupatta", category: "Accessories", size: "Free", mrp: 599, available: 60, status: "In Stock" },
  { id: "PRD-1007", name: "Wool Sweater", category: "Winter", size: "M", mrp: 2199, available: 12, status: "Returned" },
];

const columns: ColumnDef<Product, any>[] = [
  { accessorKey: "id", header: "Product ID" },
  { accessorKey: "name", header: "Name" },
  { accessorKey: "category", header: "Category" },
  { accessorKey: "size", header: "Size", enableSorting: false },
  {
    accessorKey: "mrp",
    header: "MRP",
    cell: ({ getValue }) => <span className="tabular-nums">₹{Number(getValue()).toLocaleString("en-IN")}</span>,
  },
  { accessorKey: "available", header: "Available", cell: ({ getValue }) => <span className="tabular-nums">{getValue<number>()}</span> },
  { accessorKey: "status", header: "Status", enableSorting: false, cell: ({ getValue }) => <StatusBadge status={getValue<string>()} /> },
  {
    id: "actions",
    header: "",
    enableSorting: false,
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Row actions" onClick={(e) => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => toast.success(`Viewing ${row.original.id}`)}>View</DropdownMenuItem>
          <DropdownMenuItem onClick={() => toast(`Editing ${row.original.id}`)}>Edit</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

const STEPS = ["Customer", "Items", "Payment", "Review"];

// Mock uploader so the demo never hits S3.
const mockUploader: Uploader = (fileObj, cb, _type) => {
  setTimeout(() => cb({ fileUuid: "demo-" + Math.random().toString(16).slice(2, 8) + ".png" }), 900);
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

export default function ComponentsDemo() {
  // Server-style paging over the mock set (page size 5).
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [overlay, setOverlay] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [step, setStep] = React.useState(1);

  const pageSize = 5;
  const filtered = ALL_PRODUCTS.filter((p) =>
    query ? (p.name + p.id + p.category).toLowerCase().includes(query.toLowerCase()) : true
  );
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  React.useEffect(() => {
    if (!overlay) return;
    const t = setTimeout(() => setOverlay(false), 1500);
    return () => clearTimeout(t);
  }, [overlay]);

  return (
    <div className="mz min-h-screen bg-background p-6 text-foreground md:p-10">
      <div className="mx-auto max-w-6xl space-y-10">
        <PageHeader
          title="Shared Components — Phase 2"
          description="DataTable, cards, forms, wizard, feedback — all on shadcn/Tailwind/Lucide."
          actions={
            <>
              <ThemeToggle />
              <Button onClick={() => toast.success("Primary action fired")}>
                <ShoppingBag className="h-4 w-4" />
                +Add
              </Button>
            </>
          }
        />

        <Section title="StatCards">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Total SKUs" value="1,284" caption="+3.2% vs last week" trend="up" icon={Boxes} />
            <StatCard label="Revenue" value="₹8.4L" caption="+12% MoM" trend="up" icon={IndianRupee} />
            <StatCard label="Sold Today" value="326" caption="-4% vs yesterday" trend="down" icon={TrendingUp} />
            <StatCard label="In Stock" value="942" caption="74% of catalog" icon={PackageCheck} />
          </div>
        </Section>

        <Section title="DataTable (sticky header · sort · search · pagination · mobile cards)">
          <DataTable
            columns={columns}
            data={pageRows}
            loading={loading}
            searchable
            searchPlaceholder="Search by name / ID / category"
            onSearch={(q) => {
              setQuery(q);
              setPage(1);
            }}
            pagination={{ page, total: filtered.length, pageSize, onChange: setPage }}
            onRowClick={(r) => toast(`Row: ${r.id}`)}
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setLoading((l) => !l)}>
              Toggle loading
            </Button>
            <Button variant="outline" size="sm" onClick={() => setQuery("zzz-no-match")}>
              Show empty
            </Button>
            <Button variant="outline" size="sm" onClick={() => setQuery("")}>
              Reset
            </Button>
          </div>
        </Section>

        <Section title="Status badges">
          <div className="flex flex-wrap gap-2">
            {["In Stock", "Yet to start", "Completed", "Damaged", "Payment Success", "Payment Failure", "Draft", "New", "Cancelled", "In Transit"].map(
              (s) => (
                <StatusBadge key={s} status={s} />
              )
            )}
          </div>
        </Section>

        <Section title="Wizard / Stepper">
          <Stepper steps={STEPS} current={step} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
              Back
            </Button>
            <Button size="sm" disabled={step === STEPS.length - 1} onClick={() => setStep((s) => s + 1)}>
              Next
            </Button>
          </div>
        </Section>

        <Section title="Form (card · section · field · validation slot)">
          <FormSection title="Product details" description="Grouped fields, 2-col on desktop.">
            <FieldRow label="Product name" htmlFor="pname" required>
              <Input id="pname" placeholder="Cotton Kurti" />
            </FieldRow>
            <FieldRow label="MRP" htmlFor="mrp" required error="MRP is required">
              <Input id="mrp" placeholder="1299" inputMode="numeric" />
            </FieldRow>
            <FieldRow label="Category" htmlFor="cat" hint="Pick the closest match">
              <Input id="cat" placeholder="Ethnic Wear" />
            </FieldRow>
            <FieldRow label="Available" htmlFor="avl">
              <Input id="avl" placeholder="42" inputMode="numeric" />
            </FieldRow>
            <FieldRow label="Product image" full>
              <FileDropzone type={1} uploader={mockUploader} onUploaded={(f) => toast.success(`Uploaded ${f.fileUuid}`)} hint="PNG/JPG up to 5MB" />
            </FieldRow>
          </FormSection>
        </Section>

        <Section title="Feedback: toasts · overlay · confirm · spinner · empty">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => toast.success("Saved successfully")}>
              Success toast
            </Button>
            <Button variant="outline" size="sm" onClick={() => toast.error("Something went wrong")}>
              Error toast
            </Button>
            <Button variant="outline" size="sm" onClick={() => setOverlay(true)}>
              Show overlay (1.5s)
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setConfirm(true)}>
              Delete… (confirm)
            </Button>
            <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner /> inline spinner
            </span>
          </div>
          <div className="rounded-xl border">
            <EmptyState title="No products yet" description="Add your first product to get started." action={<Button size="sm">+Add product</Button>} />
          </div>
        </Section>
      </div>

      <LoadingOverlay loading={overlay} label="Loading…" />
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Delete product?"
        description="This action cannot be undone."
        destructive
        confirmLabel="Delete"
        loading={confirmLoading}
        onConfirm={() => {
          setConfirmLoading(true);
          setTimeout(() => {
            setConfirmLoading(false);
            setConfirm(false);
            toast.success("Product deleted");
          }, 900);
        }}
      />
    </div>
  );
}
